import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import getUnicodeFlagIcon from 'country-flag-icons/unicode'
import { Check2, PencilSquare, PlusLg } from 'react-bootstrap-icons'
import { formatNumber, formatProvider, providerLogo } from '../../../../utils/Helper'
import CreatorAvatar from '../../../Shared/CreatorAvatar'
import RatingStars from '../../../Shared/RatingStars'
import TagChip from '../../../Shared/TagChip'
import ReachbirdApprovedBadge from '../../../Shared/ReachbirdApprovedBadge'
import CampaignModal from './CampaignModal'

const VISIBLE_TAGS = 6

const profilePath = authentication => `/project_manager/authentications/${authentication.id}/influencer/overview`

function Media({ authentication }) {
  const thumbnails = authentication.thumbnails || []

  if (!thumbnails.length) {
    return <CreatorAvatar src={authentication.avatar} name={authentication.username} className="h-28 w-28 shrink-0 rounded-xl" textClassName="text-2xl" />
  }

  return (
    <div className="relative h-28 w-28 shrink-0">
      <div className="grid h-full w-full grid-cols-2 grid-rows-2 gap-0.5 overflow-hidden rounded-xl bg-rb-grey dark:bg-rb-dark-mode-lighter">
        {[0, 1, 2, 3].map(index => thumbnails[index]
          ? <img key={index} src={thumbnails[index]} alt="" loading="lazy" className="h-full w-full object-cover" onError={event => { event.currentTarget.style.visibility = 'hidden' }} />
          : <div key={index} />
        )}
      </div>
      <div className="absolute -bottom-2 -right-2 rounded-full ring-4 ring-white dark:ring-rb-dark-mode">
        <CreatorAvatar src={authentication.avatar} name={authentication.username} className="h-11 w-11 rounded-full" textClassName="text-xs" />
      </div>
    </div>
  )
}

function Metric({ label, value }) {
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-xs text-rb-mat-grey dark:text-gray-400">{label}</span>
      <span className="text-sm font-semibold text-rb-black dark:text-white">{value}</span>
    </div>
  )
}

export default observer(function InfluencerItem({ authentication }) {
  const [campaignModalOpen, setCampaignModalOpen] = useState(false)
  const country = authentication.country || authentication.main_country
  const tags = authentication.tags || []
  const hiddenTagCount = Math.max(tags.length - VISIBLE_TAGS, 0)

  const openProfile = () => window.open(profilePath(authentication), '_blank')

  return (
    <div
      onClick={openProfile}
      className="group flex gap-5 rounded-xl border-1 border-rb-dash-border dark:border-rb-dark-mode-lighter bg-white dark:bg-rb-dark-mode p-4 cursor-pointer transition-shadow hover:shadow-md hover:border-rb-green/50 dark:hover:border-rb-green/50"
    >
      <Media authentication={authentication} />

      <div className="flex min-w-0 flex-1 flex-col gap-2 py-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-base" title={formatProvider(authentication.provider)}>{providerLogo(authentication.provider)}</span>
          <a
            href={profilePath(authentication)}
            target="_blank"
            rel="noreferrer"
            onClick={event => event.stopPropagation()}
            className="truncate text-base font-semibold text-rb-black dark:text-white group-hover:text-rb-green"
          >
            {authentication.username}
          </a>
          {authentication.reachbird_approved && <ReachbirdApprovedBadge />}
          {authentication.modash_updatable && (
            <span className="inline-flex items-center gap-1 h-6 rounded-2xl bg-rb-light-green dark:bg-rb-green/10 px-2.5 text-xs font-medium text-rb-green">
              <Check2 />
              Full Report
            </span>
          )}
          {authentication.manually_entered && (
            <span title="Not synced with Modash - this account's stats are entered and kept up to date by hand." className="inline-flex items-center gap-1 h-6 rounded-2xl bg-amber-100 dark:bg-amber-500/15 px-2.5 text-xs font-medium text-amber-700 dark:text-amber-400">
              <PencilSquare />
              Manual
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
          {country && (
            <span className="flex items-center gap-1 text-sm text-rb-black dark:text-white">
              {getUnicodeFlagIcon(country)}
              <span className="font-medium">{country}</span>
            </span>
          )}
          <Metric label="Reach" value={formatNumber(authentication.reach)} />
          <Metric label="Eng. rate" value={`${((authentication.engagement_rate || 0) * 100).toFixed(2)}%`} />
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
          {authentication.rating_count > 0 ? (
            <>
              <RatingStars label="Professional" value={authentication.rating_professional} />
              <RatingStars label="Quality" value={authentication.rating_quality} />
              <span className="text-xs text-rb-lighter-grey dark:text-gray-500">{authentication.rating_count} {authentication.rating_count === 1 ? 'rating' : 'ratings'}</span>
            </>
          ) : (
            <span className="text-xs text-rb-lighter-grey dark:text-gray-500">Not rated yet</span>
          )}
        </div>

        {tags.length > 0 && (
          <div className="mt-auto flex flex-wrap items-center gap-1.5">
            {tags.slice(0, VISIBLE_TAGS).map(tag => <TagChip key={tag.id} tag={tag} />)}
            {hiddenTagCount > 0 && (
              <span title={tags.slice(VISIBLE_TAGS).map(tag => tag.name).join(', ')} className="inline-flex items-center h-6 px-2.5 rounded-full bg-rb-grey dark:bg-rb-dark-mode-lighter text-xs font-semibold text-rb-mat-grey dark:text-gray-300">
                +{hiddenTagCount}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex shrink-0 flex-col items-end justify-start" onClick={event => event.stopPropagation()}>
        <button
          type="button"
          onClick={() => setCampaignModalOpen(true)}
          className="flex items-center gap-1.5 rounded-lg border-1 border-rb-green bg-white dark:bg-rb-dark-mode px-3 py-2 text-sm font-semibold text-rb-green hover:bg-rb-soft-green dark:hover:bg-rb-green/10"
        >
          <PlusLg />
          Add to campaign
        </button>
        {campaignModalOpen && <CampaignModal authentication={authentication} modalOpen={campaignModalOpen} setModalOpen={setCampaignModalOpen} />}
      </div>
    </div>
  )
})
